import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { apiError, emptyFunding, evaluateFundings, formatMinor, fundingPayload, minorToText, todayIso, toMinor } from "../../lib/finance";
import { PayerPicker } from "./FundingRows";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// A payment made after the expense was submitted: the expense itself stays frozen, this only appends one payment.
export default function RecordPaymentDialog({ expense, open, onOpenChange, onRecorded }) {
  const currency = expense.currency;
  const remainingText = expense.outstanding;
  const [row, setRow] = useState(emptyFunding());
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const key = useRef(null);

  useEffect(() => {
    if (!open) return;
    // The amount still unpaid is a sensible starting value; the payer and the date are never guessed.
    setRow(emptyFunding(minorToText(toMinor(remainingText, currency), currency).replace(".", ",")));
    setShowErrors(false);
    key.current = crypto.randomUUID(); // one key per dialog opening: a double click cannot record the payment twice
  }, [open, remainingText, currency]);

  const evaluation = evaluateFundings([row], remainingText, currency);
  const errors = showErrors ? evaluation.rowErrors[0] : {};
  const update = (patch) => setRow((old) => ({ ...old, ...patch }));

  const save = async () => {
    setShowErrors(true);
    if (!evaluation.valid) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/finance/expenses/${expense.id}/payments`,
        // The API takes a plain decimal ("6.00"): what was typed may be "6,00" or "1 250,50".
        { expected_version: expense.version, funding: { ...fundingPayload(row), amount: minorToText(toMinor(row.amount, currency), currency) } },
        { headers: { "Idempotency-Key": key.current } });
      toast.success("Paiement enregistré");
      onRecorded(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)]" data-testid="finance-payment-dialog">
        <DialogHeader>
          <DialogTitle>Enregistrer un paiement</DialogTitle>
          <DialogDescription>
            La dépense reste telle qu'elle a été soumise : seul ce paiement s'ajoute, daté et visible dans son historique.
            Reste à payer : <strong className="tabular-nums">{formatMinor(toMinor(remainingText, currency), currency)}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Qui a payé ?</Label>
            <PayerPicker funding={row} index={0} onPick={update} />
            {row.payer_type === "third_party" && (
              <Input value={row.third_party_name} onChange={(e) => update({ third_party_name: e.target.value })} maxLength={120}
                placeholder="Nom de la personne ou de l'organisation" aria-label="Nom du payeur" className="text-base md:text-sm" data-testid="finance-third-name-0" />
            )}
            {errors.payer && <p className="text-xs text-red-700">{errors.payer}</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="fin-pay-amount">Montant payé</Label>
              <div className="relative">
                <Input id="fin-pay-amount" value={row.amount} onChange={(e) => update({ amount: e.target.value })} inputMode="decimal"
                  className="pr-12 text-base md:text-sm tabular-nums" aria-invalid={!!errors.amount} data-testid="finance-funding-amount-0" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">{currency}</span>
              </div>
              {errors.amount && <p className="text-xs text-red-700">{errors.amount}</p>}
              {evaluation.overpaid && <p role="alert" className="text-xs text-red-700">Le montant dépasse le reste à payer.</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="fin-pay-date">Date du paiement</Label>
              <Input id="fin-pay-date" type="date" max={todayIso()} value={row.paid_at} onChange={(e) => update({ paid_at: e.target.value })}
                className="text-base md:text-sm" aria-invalid={!!errors.paid_at} data-testid="finance-funding-date-0" />
              {errors.paid_at && <p className="text-xs text-red-700">{errors.paid_at}</p>}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="finance-payment-save">
            {saving && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer le paiement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
